import {
	keepPreviousData,
	useInfiniteQuery,
	useQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactFormDialog } from "@/components/crm/contact-form";
import { ContactSourceBadge } from "@/components/crm/contact-source-badge";
import { ContactsWithoutCompany } from "@/components/crm/contacts-without-company";
import { CrmLoadError, CrmPageHeader } from "@/components/crm/crm-page";
import { LoadMore, nextOffset } from "@/components/crm/load-more";
import {
	OwnerFilter,
	type OwnerFilterValue,
	resolveOwnerFilter,
} from "@/components/crm/owner-select";
import { PlatformUserBadge } from "@/components/crm/platform-user-badge";
import { SortableHead, useListSort } from "@/components/crm/sortable-head";
import { ContactStatusBadge } from "@/components/crm/status-badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "@/hooks/auth-hooks";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { CRM_CONTACT_SOURCES, CRM_CONTACT_STATUSES } from "@/lib/crm/constants";
import { contactName } from "@/lib/crm/format";
import { CRM_STALE_TIME } from "@/lib/crm/queries";
import { CRM_TAB_TRIGGER, CRM_TABS_LIST } from "@/lib/crm/tabs";
import type { CrmContactSource, CrmContactStatus } from "@/lib/crm/types";
import { formatTimeAgo } from "@/lib/sales-format";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/contacts/")({
	component: ContactsPage,
});

const PAGE_SIZE = 50;
const ALL = "__all";

type FilterSelectProps<T extends string> = {
	value: T | null;
	values: readonly T[];
	onChange: (value: T | null) => void;
	allLabel: string;
	label: string;
	labelFor: (value: T) => string;
};

const FilterSelect = <T extends string>({
	value,
	values,
	onChange,
	allLabel,
	label,
	labelFor,
}: FilterSelectProps<T>): ReactElement => (
	<Select
		value={value ?? ALL}
		onValueChange={(next) => onChange(next === ALL ? null : (next as T))}
	>
		<SelectTrigger className="w-44" aria-label={label}>
			<SelectValue />
		</SelectTrigger>
		<SelectContent>
			<SelectItem value={ALL}>{allLabel}</SelectItem>
			{values.map((option) => (
				<SelectItem key={option} value={option}>
					{labelFor(option)}
				</SelectItem>
			))}
		</SelectContent>
	</Select>
);

type ContactSort =
	| "name"
	| "email"
	| "company"
	| "source"
	| "lastActivity"
	| "owner";

function ContactsPage(): ReactElement {
	const { t, i18n } = useTranslation();
	const { canManage } = useCrmPermissions();
	const { data: session } = useSession();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [status, setStatus] = useState<CrmContactStatus | null>(null);
	const [source, setSource] = useState<CrmContactSource | null>(null);
	const [owner, setOwner] = useState<OwnerFilterValue>("all");
	const [newContactOpen, setNewContactOpen] = useState(false);
	const [tab, setTab] = useState("all");
	// The tab label's count (contacts without a company).
	const { data: withoutCompany } = useQuery({
		...orpc.admin.crm.contacts.withoutCompany.queryOptions(),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});
	const sorting = useListSort<ContactSort>("lastActivity", {
		descFirst: ["lastActivity"],
	});

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		...orpc.admin.crm.contacts.list.infiniteOptions({
			input: (offset: number) => ({
				search: debouncedSearch || undefined,
				status: status ?? undefined,
				source: source ?? undefined,
				ownerId: resolveOwnerFilter(owner, session?.user.id),
				sort: sorting.sort,
				direction: sorting.direction,
				limit: PAGE_SIZE,
				offset,
			}),
			initialPageParam: 0,
			getNextPageParam: (lastPage, pages) =>
				nextOffset(pages, lastPage.total, (page) => page.contacts.length),
		}),
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});

	const rows = data?.pages.flatMap((page) => page.contacts) ?? [];
	const total = data?.pages[0]?.total ?? 0;
	const list = (
		<>
			<div className="flex flex-wrap gap-2">
				<Input
					placeholder={t("admin:crm.contacts.search_placeholder")}
					aria-label={t("admin:crm.contacts.search_placeholder")}
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					className="max-w-sm"
				/>
				<FilterSelect
					value={status}
					values={CRM_CONTACT_STATUSES}
					onChange={setStatus}
					allLabel={t("admin:crm.contacts.all_statuses")}
					label={t("admin:crm.contact.status")}
					labelFor={(value) => t(`admin:crm.contact_status.${value}`)}
				/>
				<FilterSelect
					value={source}
					values={CRM_CONTACT_SOURCES}
					onChange={setSource}
					allLabel={t("admin:crm.contacts.all_sources")}
					label={t("admin:crm.common.source")}
					labelFor={(value) => t(`admin:crm.contact_source.${value}`)}
				/>
				<OwnerFilter value={owner} onChange={setOwner} />
			</div>
			{isLoading ? (
				<div className="space-y-2">
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
				</div>
			) : isError || !data ? (
				<CrmLoadError />
			) : rows.length === 0 ? (
				<p className="py-8 text-center text-muted-foreground text-sm">
					{t("admin:crm.contacts.empty")}
				</p>
			) : (
				<>
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead
									label={t("admin:crm.contact.name")}
									sortKey="name"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.contact.email")}
									sortKey="email"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.contact.company")}
									sortKey="company"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.common.source")}
									sortKey="source"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.contacts.last_activity")}
									sortKey="lastActivity"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.common.owner")}
									sortKey="owner"
									{...sorting}
								/>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((contact) => (
								<TableRow key={contact.id}>
									<TableCell>
										<div className="flex items-center gap-2">
											<Link
												to="/crm/contacts/$contactId"
												params={{ contactId: contact.id }}
												className="font-medium hover:underline"
											>
												{contactName({
													firstName: contact.firstName,
													lastName: contact.lastName,
												}) ??
													(contact.status === "erased"
														? t("admin:crm.contact.erased_name")
														: "—")}
											</Link>
											<ContactStatusBadge status={contact.status} />
											{contact.platformUserId && <PlatformUserBadge compact />}
										</div>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{contact.email ?? "—"}
									</TableCell>
									<TableCell>
										{contact.organizationId ? (
											<Link
												to="/crm/organizations/$organizationId"
												params={{ organizationId: contact.organizationId }}
												className="hover:underline"
											>
												{contact.organizationName}
											</Link>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell>
										<ContactSourceBadge source={contact.source} />
									</TableCell>
									<TableCell>
										{formatTimeAgo(
											new Date(contact.lastActivityAt),
											i18n.language,
										)}
									</TableCell>
									<TableCell>{contact.ownerName ?? "—"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					<LoadMore
						loaded={rows.length}
						total={total}
						hasMore={hasNextPage}
						loading={isFetchingNextPage}
						onLoadMore={() => void fetchNextPage()}
					/>
				</>
			)}
		</>
	);

	return (
		<div className="space-y-4">
			<CrmPageHeader
				title={t("admin:crm.contacts.title")}
				subtitle={t("admin:crm.contacts.subtitle")}
				actions={
					canManage && (
						<Button
							onClick={() => setNewContactOpen(true)}
							data-testid="new-contact-button"
						>
							<UserPlus className="h-4 w-4" />
							{t("admin:crm.contacts.new_contact")}
						</Button>
					)
				}
			/>
			{canManage ? (
				<Tabs value={tab} onValueChange={setTab}>
					<TabsList className={CRM_TABS_LIST}>
						<TabsTrigger className={CRM_TAB_TRIGGER} value="all">
							{t("admin:crm.contacts.tab_all")}
						</TabsTrigger>
						<TabsTrigger className={CRM_TAB_TRIGGER} value="without_company">
							{t("admin:crm.without_company.tab", {
								count: withoutCompany?.total ?? 0,
							})}
						</TabsTrigger>
					</TabsList>
					<TabsContent value="all" className="space-y-4">
						{list}
					</TabsContent>
					<TabsContent value="without_company">
						<ContactsWithoutCompany />
					</TabsContent>
				</Tabs>
			) : (
				list
			)}
			<ContactFormDialog
				open={newContactOpen}
				onOpenChange={setNewContactOpen}
			/>
		</div>
	);
}
